import React, { FunctionComponent, useMemo, useState } from "react";
import { Select } from "antd";
import { withCustomOption } from "./JobForm/useMasterListSearch";

export interface LocationInputProps {
  // `id`, `value` and `onChange` are injected by the surrounding Form.Item.
  id?: string;
  value?: string;
  onChange?: (value: string | undefined) => void;
  placeholder: string;
  /** The `city` slug's names, read through `useMasterLists`. */
  locations: string[];
  loading: boolean;
  failed: boolean;
  onRetry: () => void;
  allowClear?: boolean;
  disabled?: boolean;
}

/**
 * Each place name once (trimmed, case ignored, the first spelling kept). The `city` list holds several
 * entries with the same name (e.g. two "Khargone" rows with different ids); the name is this picker's
 * option value, and antd's virtual list keys its rows by value, so a repeated name made it reuse the
 * wrong rows: one name shown over and over, other places missing, a stale row left after a search.
 */
export const uniqueLocationNames = (names: string[]): string[] => {
  const seen = new Set<string>();
  return (names || []).reduce<string[]>((unique, raw) => {
    const name = typeof raw === "string" ? raw.trim() : "";
    const key = name.toLowerCase();
    if (name && !seen.has(key)) {
      seen.add(key);
      unique.push(name);
    }
    return unique;
  }, []);
};

/**
 * The off-campus request form's Location.
 *
 * The locations list has no id to send, so the value here is the place name itself — the same plain
 * string a short text answer already is. Nothing about the submission or the saved-submission round
 * trip changes for this field: it is read and written by the shared helpers like any other text answer.
 *
 * The whole list arrives in the form's one master-list read and is filtered in the browser,
 * so typing costs no requests and needs no debounce. A name that is not on the list is offered as the
 * "use what you typed" option (`withCustomOption`, the job form's helper). Location stays open
 * where Company is closed to its list: these names are submitted as names, nothing downstream
 * resolves them to a record, and a student interning somewhere the list omits must still be able to
 * submit — which is also why a failed read leaves this field fully usable and only says so.
 */
const LocationInput: FunctionComponent<LocationInputProps> = ({
  id,
  value,
  onChange,
  placeholder,
  locations,
  loading,
  failed,
  onRetry,
  allowClear,
  disabled
}) => {
  // The latest typed term, offered as a free-text option when no place name matches it.
  const [term, setTerm] = useState("");

  const names = useMemo(() => uniqueLocationNames(locations), [locations]);
  const needle = term.trim().toLowerCase();
  const matches = (needle ? names.filter(name => name.toLowerCase().includes(needle)) : names).map(name => ({ label: name, value: name }));

  // The answer is a plain string that antd's Select does not keep in `options` once the typed term is
  // cleared, so the current value is always offered too: the picked place keeps showing as itself.
  const baseOptions = withCustomOption(matches, term);
  const options = value && !baseOptions.some(option => option.value === value) ? [...baseOptions, { label: value, value }] : baseOptions;

  // antd drops search text that was typed but never picked when the field loses focus. A place the
  // learner typed and left is still their answer, so it is committed here (a listed name keeps the
  // list's spelling).
  const commitTyped = () => {
    const typed = term.trim();
    if (!typed) return;
    setTerm("");
    const listed = names.find(name => name.toLowerCase() === typed.toLowerCase());
    const next = listed ?? typed;
    if (next !== value) onChange?.(next);
  };

  let notFoundContent: React.ReactNode = "Type to add your location";
  // Not the shared notice: the dropdown is where the learner already is, and typing is the whole
  // answer here, so it says that first. The retry sits under the field, where it does not fight the
  // free-text option for the same click.
  if (failed) notFoundContent = "Type your location";
  else if (loading) notFoundContent = "Loading…";

  return (
    <>
      <Select<string>
        id={id}
        showSearch
        disabled={disabled}
        allowClear={allowClear}
        filterOption={false}
        placeholder={placeholder}
        value={value || undefined}
        onSearch={setTerm}
        onChange={next => {
          setTerm("");
          onChange?.(next || undefined);
        }}
        loading={loading}
        onBlur={commitTyped}
        options={options}
        notFoundContent={notFoundContent}
      />
    </>
  );
};

export default LocationInput;
